import React, { useState } from 'react';
import { User, Mail, Lock, Save, X, AlertTriangle, Trash2, ArrowLeft } from 'lucide-react';
import * as api from '../services/api';

const Profile = ({ user, workerApiUrl, onBack, onAccountDeleted, onManageSubscription }) => {
  // Edit states
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Form values
  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleUpdateName = async () => {
    if (!name.trim() || name === user.name) {
      setIsEditingName(false);
      setName(user.name || '');
      return;
    }

    setIsSaving(true);
    try {
      await api.updateProfile(workerApiUrl, { name: name.trim() });
      alert('Name updated successfully!');
      setIsEditingName(false);
      // Refresh user data
      window.location.reload();
    } catch (err) {
      alert(`Failed to update name: ${err.message}`);
      setName(user.name || '');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!email.trim() || email === user.email) {
      setIsEditingEmail(false);
      setEmail(user.email || '');
      return;
    }

    setIsSaving(true);
    try {
      await api.updateProfile(workerApiUrl, { email: email.trim() });
      alert('Email updated successfully! Please verify your new email address.');
      setIsEditingEmail(false);
      // Refresh user data
      window.location.reload();
    } catch (err) {
      alert(`Failed to update email: ${err.message}`);
      setEmail(user.email || '');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      alert('New passwords do not match!');
      return;
    }

    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters long!');
      return;
    }

    setIsSaving(true);
    try {
      await api.changePassword(workerApiUrl, {
        currentPassword,
        newPassword
      });
      alert('Password changed successfully!');
      setIsChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      alert(`Failed to change password: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await api.deleteAccount(workerApiUrl);
      alert('Account deleted successfully');
      setShowDeleteConfirm(false);
      if (onAccountDeleted) {
        onAccountDeleted();
      }
    } catch (err) {
      alert(`Failed to delete account: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="profile-view">
      {/* Header */}
      <div className="profile-header">
        <button onClick={onBack} className="back-button">
          <ArrowLeft size={18} />
          Back to Dashboard
        </button>
        <h1>Account Settings</h1>
      </div>

      {/* Profile Information Section */}
      <section className="profile-section">
        <div className="section-header">
          <User size={20} />
          <h2>Profile Information</h2>
        </div>

        <div className="settings-card">
          {/* Name */}
          <div className="setting-item">
            <label>Full Name</label>
            <div className="setting-value">
              {isEditingName ? (
                <div className="edit-name-container">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="name-input"
                    autoFocus
                  />
                  <button 
                    className="btn-icon-primary" 
                    onClick={handleUpdateName}
                    disabled={isSaving}
                  >
                    <Save size={16} />
                  </button>
                  <button 
                    className="btn-icon-secondary" 
                    onClick={() => {
                      setIsEditingName(false);
                      setName(user.name || '');
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="name-display">
                  <span className="app-name-text">{user.name || 'Not set'}</span>
                  <button 
                    className="btn-secondary" 
                    onClick={() => setIsEditingName(true)}
                  >
                    Edit Name
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="setting-item">
            <label>Email Address</label>
            <div className="setting-value">
              {isEditingEmail ? (
                <div className="edit-name-container">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="name-input"
                    autoFocus
                  />
                  <button 
                    className="btn-icon-primary" 
                    onClick={handleUpdateEmail}
                    disabled={isSaving}
                  >
                    <Save size={16} />
                  </button>
                  <button 
                    className="btn-icon-secondary" 
                    onClick={() => {
                      setIsEditingEmail(false);
                      setEmail(user.email || '');
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="name-display">
                  <span className="app-name-text">{user.email}</span>
                  <button 
                    className="btn-secondary" 
                    onClick={() => setIsEditingEmail(true)}
                  >
                    Edit Email
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* User ID */}
          <div className="setting-item">
            <label>User ID</label>
            <div className="setting-value">
              <code className="app-id-code">{user.id}</code>
            </div>
          </div>

          {/* Account Created */}
          {user.created_at && (
            <div className="setting-item">
              <label>Account Created</label>
              <div className="setting-value">
                <span>{formatDate(user.created_at)}</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Subscription Information Section */}
      <section className="profile-section">
        <div className="section-header">
          <Lock size={20} /> {/* Reusing Lock icon for now, consider a new icon like 'CreditCard' or 'DollarSign' */}
          <h2>Subscription Plan</h2>
        </div>

        <div className="settings-card">
          <div className="setting-item">
            <label>Current Plan</label>
            <div className="setting-value">
              <span className="app-name-text">{user.plan?.name || 'Free'}</span>
              <button 
                className="btn-secondary" 
                onClick={onManageSubscription}
              >
                Manage Subscription
              </button>
            </div>
          </div>
          {user.plan && user.plan.name !== 'Free' && (
            <div className="setting-item">
              <label>Price</label>
              <div className="setting-value">
                <span>{user.plan.currency} {user.plan.price.toFixed(2)} / month</span>
              </div>
            </div>
          )}
          {user.plan && user.plan.features && user.plan.features.length > 0 && (
            <div className="setting-item">
              <label>Features</label>
              <div className="setting-value">
                <ul className="feature-list">
                  {user.plan.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Security Section */}
      <section className="profile-section">
        <div className="section-header">
          <Lock size={20} />
          <h2>Security</h2>
        </div>

        <div className="settings-card">
          {!isChangingPassword ? (
            <div className="setting-item">
              <label>Password</label>
              <div className="setting-value">
                <div className="name-display">
                  <span className="app-name-text">••••••••</span>
                  <button 
                    className="btn-secondary" 
                    onClick={() => setIsChangingPassword(true)}
                  >
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleChangePassword} className="password-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength="6"
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength="6"
                  autoComplete="new-password"
                />
              </div>
              <div className="form-actions">
                <button 
                  type="button"
                  className="btn-secondary" 
                  onClick={() => {
                    setIsChangingPassword(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn-primary" 
                  disabled={isSaving}
                >
                  {isSaving ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Danger Zone Section */}
      <section className="profile-section danger-zone">
        <div className="section-header">
          <AlertTriangle size={20} />
          <h2>Danger Zone</h2>
        </div>

        <div className="danger-card">
          <div className="danger-content">
            <div>
              <h3>Delete Account</h3>
              <p>
                Once you delete your account, there is no going back. This will permanently delete
                all your applications, logs, tokens, and any other data associated with your account.
              </p>
            </div>
            <button 
              className="btn-danger" 
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 size={16} />
              <span className="btn-text">Delete Account</span>
            </button>
          </div>
        </div>
      </section>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteConfirm(false)}>
          <div className="modal danger-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <AlertTriangle size={24} color="#ef4444" />
              <h2>Delete Account</h2>
            </div>
            <div className="modal-body">
              <p>
                Are you absolutely sure you want to delete your account?
              </p>
              <p className="warning-text">
                This action cannot be undone. This will permanently delete:
              </p>
              <ul className="delete-list">
                <li>All your applications and their settings</li>
                <li>All logs and analytics data</li>
                <li>All API tokens</li>
                <li>Your account and profile information</li>
              </ul>
              <div className="confirmation-input">
                <label>
                  Type <strong>DELETE</strong> to confirm:
                </label>
                <input
                  type="text"
                  placeholder="DELETE"
                  id="deleteAccountInput"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="btn-danger" 
                onClick={() => {
                  const input = document.getElementById('deleteAccountInput');
                  if (input.value === 'DELETE') {
                    handleDeleteAccount();
                  } else {
                    alert('Please type DELETE to confirm');
                  }
                }}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;

